import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import { useSnackbar } from 'notistack';
import { fDateTime, fToNow } from 'src/utils/formatTime';
import DrawerNegocioNotasInput from './DrawerNegocioNotasInput';
import v2_actionAddNotasLead from 'src/actions/v2/crm/v2_actionAddNotasLead';
import v2_actionDeletarNotaLead from 'src/actions/v2/crm/v2_actionDeletarNotaLead';
import v2_actionEditarNotaLead from 'src/actions/v2/crm/v2_actionEditarNotaLead';
import { ScrollArea } from 'src/@/components/ui/scroll-area';
import { Button } from 'src/@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from 'src/@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from 'src/@/components/ui/dropdown-menu';
import { Textarea } from 'src/@/components/ui/textarea';
import Iconify from 'src/components/Iconify';
import { cn } from 'src/@/lib/utils';
import createAvatar from 'src/utils/createAvatar';

const AVATAR_COLORS = {
  primary: 'bg-violet-100 text-violet-700 dark:bg-violet-500/15 dark:text-violet-400',
  info: 'bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-400',
  success: 'bg-green-100 text-green-700 dark:bg-green-500/15 dark:text-green-400',
  warning: 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400',
  error: 'bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-400',
  default: 'bg-gray-100 text-gray-600 dark:bg-[#383838] dark:text-[#ABABAD]'
};

DrawerNegocioNotas.propTypes = {
  dataLead: PropTypes.object,
  user: PropTypes.object,
  refreshLead: PropTypes.func
};

export default function DrawerNegocioNotas({ dataLead, user, refreshLead }) {
  const [text, setText] = useState('');
  const { enqueueSnackbar } = useSnackbar();
  const [notas, setNotas] = useState(dataLead?.notas || []);
  const [editingNota, setEditingNota] = useState(null);
  const [editText, setEditText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (dataLead?.notas) {
      setNotas(dataLead.notas);
    }
  }, [dataLead]);

  // Mais recente primeiro: o comentário novo aparece logo abaixo do campo de escrita
  const notasOrdenadas = [...notas].sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));

  async function novaNota() {
    try {
      const data = { nota: { body: text, usuario_id: user.id, date: new Date() } };
      const response = await v2_actionAddNotasLead(dataLead._id, data);
      setText('');
      if (response.data && response.data.data) {
        setNotas([...notas, response.data.data]);
      } else {
        const dataPreview = { body: text, usuario_id: user.id, date: new Date() };
        setNotas([...notas, dataPreview]);
      }
      enqueueSnackbar('Comentário adicionado com sucesso', {
        variant: 'success',
        autoHideDuration: 3000,
        anchorOrigin: { vertical: 'top', horizontal: 'right' }
      });
      refreshLead();
    } catch (error) {
      console.log(error);
      enqueueSnackbar('Erro ao adicionar comentário', {
        variant: 'error',
        autoHideDuration: 3000,
        anchorOrigin: { vertical: 'top', horizontal: 'right' }
      });
    }
  }

  function handleEditClick(nota) {
    setEditingNota(nota);
    setEditText(nota.body);
  }

  function handleCancelEdit() {
    setEditingNota(null);
    setEditText('');
  }

  async function handleSaveEdit() {
    if (!editingNota || !editText.trim()) return;
    try {
      const data = { nota: { body: editText.trim() } };
      await v2_actionEditarNotaLead(editingNota._id, data);
      setNotas(notas.map((nota) => (nota._id === editingNota._id ? { ...nota, body: editText.trim() } : nota)));
      handleCancelEdit();
      enqueueSnackbar('Comentário editado com sucesso', {
        variant: 'success',
        autoHideDuration: 3000,
        anchorOrigin: { vertical: 'top', horizontal: 'right' }
      });
      refreshLead();
    } catch (error) {
      console.log(error);
      enqueueSnackbar('Erro ao editar comentário', {
        variant: 'error',
        autoHideDuration: 3000,
        anchorOrigin: { vertical: 'top', horizontal: 'right' }
      });
    }
  }

  async function handleDelete(notaId) {
    if (!notaId) {
      enqueueSnackbar('Erro: Identificador do comentário inválido.', { variant: 'error' });
      return;
    }
    if (!confirm('Tem certeza que deseja excluir este comentário?')) return;
    try {
      setIsDeleting(true);
      await v2_actionDeletarNotaLead(notaId);
      setNotas(notas.filter((nota) => nota._id !== notaId));
      enqueueSnackbar('Comentário excluído com sucesso', {
        variant: 'success',
        autoHideDuration: 3000,
        anchorOrigin: { vertical: 'top', horizontal: 'right' }
      });
      refreshLead();
    } catch (error) {
      console.log(error);
      enqueueSnackbar('Erro ao excluir comentário', {
        variant: 'error',
        autoHideDuration: 3000,
        anchorOrigin: { vertical: 'top', horizontal: 'right' }
      });
    } finally {
      setIsDeleting(false);
    }
  }

  const canEditDelete = (nota) => {
    return user && nota.usuario_id === user.id;
  };

  const getUsuarioIniciais = (nota) => {
    if (nota.usuario_nome) {
      const names = nota.usuario_nome.split(' ').filter(Boolean);
      if (names.length >= 2) {
        return `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase();
      }
      return nota.usuario_nome.substring(0, 2).toUpperCase();
    }
    return 'U';
  };

  const getAvatarColor = (nota) => {
    const avatar = createAvatar(nota.usuario_nome || 'Usuario');
    return AVATAR_COLORS[avatar.color] || AVATAR_COLORS.default;
  };

  return (
    <div className="flex flex-col h-full">
      <DrawerNegocioNotasInput text={text} changeText={(e) => setText(e.target.value)} onSend={novaNota} />

      <ScrollArea
        className={cn(
          'flex-1 mt-2',
          'h-[calc(50vh-100px)] sm:h-[calc(50vh-100px)] md:h-[calc(50vh-100px)] lg:h-[calc(60vh-100px)] xl:h-[calc(60vh-100px)]'
        )}
      >
        {notasOrdenadas.length > 0 ? (
          <div className="divide-y divide-gray-100 dark:divide-[#383838]/60 pr-3">
            {notasOrdenadas.map((nota, index) => {
              const isEditing = editingNota && editingNota._id === nota._id;

              return (
                <div key={nota._id || index} className="group flex gap-3 py-4">
                  <Avatar className="h-8 w-8 shrink-0 mt-0.5">
                    <AvatarImage
                      src={nota.usuario_foto || nota.usuario_photoURL}
                      alt={nota.usuario_nome || 'Usuário'}
                    />
                    <AvatarFallback className={cn('text-[11px] font-semibold', getAvatarColor(nota))}>
                      {getUsuarioIniciais(nota)}
                    </AvatarFallback>
                  </Avatar>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-[13px] font-semibold text-gray-900 dark:text-[#D1D2D3] truncate">
                        {nota.usuario_nome || nota.usuario_displayName || 'Usuário'}
                      </span>
                      {nota.date && (
                        <span
                          className="text-[11px] text-gray-400 dark:text-[#ABABAD]/70 whitespace-nowrap"
                          title={fDateTime(nota.date)}
                        >
                          {fToNow(nota.date)}
                        </span>
                      )}

                      {canEditDelete(nota) && !isEditing && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 ml-auto shrink-0 rounded-md text-gray-400 dark:text-[#ABABAD] opacity-0 group-hover:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100 transition-opacity hover:bg-gray-100 dark:hover:bg-[#383838]"
                            >
                              <Iconify icon="mdi:dots-horizontal" className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleEditClick(nota)} className="cursor-pointer">
                              <Iconify icon="mdi:pencil-outline" className="h-4 w-4 mr-2" />
                              Editar
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleDelete(nota._id)}
                              className="cursor-pointer text-destructive focus:text-destructive"
                              disabled={isDeleting}
                            >
                              <Iconify icon="mdi:delete-outline" className="h-4 w-4 mr-2" />
                              Excluir
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>

                    {isEditing ? (
                      <div className="mt-2 space-y-2">
                        <Textarea
                          autoFocus
                          value={editText}
                          onChange={(e) => setEditText(e.target.value)}
                          placeholder="Digite seu comentário…"
                          className="min-h-[80px] text-sm rounded-xl border-gray-200 dark:border-[#383838] dark:bg-[#1A1D21] dark:text-[#D1D2D3] focus-visible:ring-[#7C3AED]/20"
                        />
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleCancelEdit}
                            className="h-7 px-3 text-xs text-gray-600 dark:text-[#ABABAD]"
                          >
                            Cancelar
                          </Button>
                          <Button
                            size="sm"
                            onClick={handleSaveEdit}
                            disabled={!editText.trim()}
                            className="h-7 px-3 text-xs bg-[#7C3AED] hover:bg-[#6d28d9] text-white"
                          >
                            Salvar
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <p className="mt-0.5 text-sm text-gray-700 dark:text-[#D1D2D3]/90 whitespace-pre-wrap break-words leading-relaxed">
                        {nota.body}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-14 text-center">
            <div className="w-12 h-12 rounded-2xl bg-gray-50 dark:bg-[#222529] flex items-center justify-center mb-3">
              <Iconify icon="eva:message-square-outline" width={22} className="text-gray-300 dark:text-[#ABABAD]/40" />
            </div>
            <p className="text-sm font-medium text-gray-600 dark:text-[#ABABAD]">Nenhum comentário ainda</p>
            <p className="text-xs text-gray-400 dark:text-[#ABABAD]/60 mt-1">Escreva o primeiro comentário acima</p>
          </div>
        )}
      </ScrollArea>
    </div>
  );
}
